/**
 * The four KPI cards above the Disbursed Loan Details register
 * (plans/disbursed_loans_dashboard_plan.md §5.3).
 *
 * Total Disbursed (amount) | Total Loans (count) | MTD | vs Last Month.
 *
 * DATA SOURCE
 * -----------
 * A `DisbursedLoanMetrics` object from
 * GET /api/lms/staff_ops/disbursed_loans/metrics/ (backend `build_metrics()`).
 * It is passed IN as a prop rather than fetched here: the parent owns one shared
 * `useQuery(["lms-disbursed-loans-metrics"])` for the cards AND the chart, so the
 * endpoint is hit once per screen.
 *
 * THE THREE THINGS THIS COMPONENT EXISTS TO PRESENT HONESTLY
 * ---------------------------------------------------------
 * 1. **The compared periods are spelled out.** The comparison basis is "same
 *    period last month, day-aligned and clamped" — a deliberate choice by the
 *    requester. "+33%" with an unstated baseline is a number nobody can check,
 *    so the card always renders "1–4 Aug vs 1–4 Jul".
 *
 * 2. **`amount_pct`/`count_pct` are `number | null`.** `null` means last month's
 *    same period was ZERO, where a percentage is undefined. It renders as "New"
 *    with the absolute delta — never `NaN%`, never `Infinity%`, and never a
 *    fabricated 0% or 100% that someone would act on.
 *
 * 3. **`total.undated_count` is footnoted on the Total card.** Loans with a null
 *    `date_of_disbursal` are in the all-time total but cannot belong to any
 *    month, so they are absent from MTD/series/forecast. Without the footnote,
 *    "Total 412" beside a series accounting for 409 reads as a dashboard bug;
 *    with it, the two figures visibly reconcile.
 *
 * Money arrives as DECIMAL STRINGS and is only ever passed to a formatter, never
 * into arithmetic here — the sign of a delta is taken from `Number()` at the
 * point of comparison only.
 */
import React from "react";
import {
    IndianRupee,
    FileText,
    CalendarRange,
    TrendingUp,
    TrendingDown,
    Minus,
} from "lucide-react";

import { DisbursedLoanMetrics } from "./lmsStaffApi";
import { formatAmount, formatCompactAmount, formatDayRange } from "./formatters";

interface DisbursedLoansKpiCardsProps {
    metrics?: DisbursedLoanMetrics;
    isLoading?: boolean;
    /** Set when the shared metrics query failed; the cards degrade quietly. */
    isError?: boolean;
    /**
     * When set (format `"YYYY-MM"`), the MTD card label shows the selected
     * month name instead of "Month to Date". Empty string / undefined means
     * live MTD.
     */
    referenceMonth?: string;
}

/** Four grey blocks while the shared metrics query is in flight. */
const CardSkeleton: React.FC = () => (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="h-3 w-24 animate-pulse rounded bg-gray-200" />
        <div className="mt-3 h-7 w-32 animate-pulse rounded bg-gray-200" />
        <div className="mt-3 h-3 w-40 animate-pulse rounded bg-gray-100" />
    </div>
);

const CARD_SHELL =
    "rounded-xl border border-gray-200 bg-white p-4 shadow-sm flex flex-col justify-between min-h-[120px]";

const DisbursedLoansKpiCards: React.FC<DisbursedLoansKpiCardsProps> = ({
    metrics,
    isLoading,
    isError,
    referenceMonth,
}) => {
    if (isLoading && !metrics) {
        return (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <CardSkeleton />
                <CardSkeleton />
                <CardSkeleton />
                <CardSkeleton />
            </div>
        );
    }

    // A metrics failure must not take the register down with it: the table is the
    // primary content and has its own error handling (including the distinct 403
    // remedy path). A single explanatory strip is the right amount of noise.
    if (isError || !metrics) {
        return (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-center text-xs text-gray-500">
                Disbursal summary is unavailable right now. The register below is
                unaffected.
            </div>
        );
    }

    const { total, mtd, last_month_same_period, last_month_full, performance } =
        metrics;

    /**
     * When a past month is selected, show its short name instead of the
     * generic "Month to Date" label, e.g. "SEP 2026".
     * Derived from `referenceMonth + "-01"` so the Date constructor parses
     * it as a local date rather than UTC midnight (which would shift the
     * month on UTC+0 machines).
     */
    const mtdCardLabel = referenceMonth
        ? new Date(referenceMonth + "-01")
              .toLocaleString("en-IN", { month: "short", year: "numeric" })
              .toUpperCase()
        : "Month to Date";

    // MTD ÷ last month full — the "pace" multiplier.
    // Shows how many times the current MTD already is relative to the entire
    // previous month. Shown as "2.62x" (2 dp). "—" when last month was zero.
    const lastFullAmountNum = Number(last_month_full.amount);
    const mtdAmountNum = Number(mtd.amount);
    const mtdVsFullMultiplier =
        lastFullAmountNum > 0
            ? (mtdAmountNum / lastFullAmountNum).toFixed(2)
            : null;

    const lastFullCountNum = last_month_full.count;
    const mtdCountNum = mtd.count;
    const countMultiplier =
        lastFullCountNum > 0
            ? (mtdCountNum / lastFullCountNum).toFixed(2)
            : null;

    // The delta's SIGN drives the arrow and the colour. Taken from Number() on
    // the decimal string at the point of comparison only — the displayed value
    // stays the exact string the backend computed.
    const amountDelta = Number(performance.amount_delta);
    const amountUp = amountDelta > 0;
    const amountFlat = amountDelta === 0;

    const mtdRange = formatDayRange(mtd.period_start, mtd.period_end);
    const lastRange = formatDayRange(
        last_month_same_period.period_start,
        last_month_same_period.period_end
    );

    /**
     * "+33.3%" / "New" / "—".
     *
     * `null` pct (a zero baseline) becomes "New" when we actually grew and "—"
     * when nothing happened either month, because "New" against 0 vs 0 would be
     * a claim of activity that did not occur.
     */
    const pctLabel = (pct: number | null, delta: number): string => {
        if (pct === null) return delta > 0 ? "New" : "—";
        const sign = pct > 0 ? "+" : "";
        return `${sign}${pct.toFixed(1)}%`;
    };

    const TrendIcon = amountFlat ? Minus : amountUp ? TrendingUp : TrendingDown;
    const trendColour = amountFlat
        ? "text-gray-500"
        : amountUp
          ? "text-emerald-600"
          : "text-red-600";
    const trendBg = amountFlat
        ? "bg-gray-100"
        : amountUp
          ? "bg-emerald-50"
          : "bg-red-50";

    return (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {/* 1 — Total Disbursed (all time, amount) */}
            <div className={CARD_SHELL}>
                <div className="flex items-start justify-between">
                    <div>
                        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                            Total Disbursed
                        </p>
                        <p
                            className="mt-1 text-2xl font-bold text-gray-900"
                            title={formatAmount(total.amount)}
                        >
                            {formatCompactAmount(total.amount)}
                        </p>
                    </div>
                    <span className="rounded-lg bg-intellend-green/10 p-2">
                        <IndianRupee className="h-4 w-4 text-intellend-green" />
                    </span>
                </div>
                {/*
                  The reconciliation footnote (plan §4.3 case 2). Only shown when
                  it actually applies, so it never becomes decorative noise.
                */}
                {total.undated_count > 0 ? (
                    <p className="mt-2 text-[11px] leading-snug text-amber-700">
                        {total.undated_count.toLocaleString("en-IN")}{" "}
                        {total.undated_count === 1 ? "loan has" : "loans have"} no
                        disbursal date and{" "}
                        {total.undated_count === 1 ? "is" : "are"} excluded from MTD
                        and the chart.
                    </p>
                ) : (
                    <p className="mt-2 text-[11px] text-gray-400">
                        All time, excluding simulation loans.
                    </p>
                )}
            </div>

            {/* 2 — Total Loans (all time, count) */}
            <div className={CARD_SHELL}>
                <div className="flex items-start justify-between">
                    <div>
                        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                            Total Loans
                        </p>
                        <p className="mt-1 text-2xl font-bold text-gray-900">
                            {total.count.toLocaleString("en-IN")}
                        </p>
                    </div>
                    <span className="rounded-lg bg-blue-50 p-2">
                        <FileText className="h-4 w-4 text-blue-600" />
                    </span>
                </div>
                <p className="mt-2 text-[11px] text-gray-400">
                    Disbursed loans on record, including closed ones.
                </p>
            </div>

            {/* 3 — Month to date (or selected past month) */}
            <div className={CARD_SHELL}>
                <div className="flex items-start justify-between">
                    <div>
                        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                            {mtdCardLabel}
                        </p>
                        <p
                            className="mt-1 text-2xl font-bold text-gray-900"
                            title={formatAmount(mtd.amount)}
                        >
                            {formatCompactAmount(mtd.amount)}
                        </p>
                    </div>
                    <span className="rounded-lg bg-violet-50 p-2">
                        <CalendarRange className="h-4 w-4 text-violet-600" />
                    </span>
                </div>
                <p className="mt-2 text-[11px] text-gray-500">
                    {mtd.count.toLocaleString("en-IN")}{" "}
                    {mtd.count === 1 ? "loan" : "loans"}
                    {mtdRange ? ` · ${mtdRange}` : ""}
                </p>
            </div>

            {/* 4 — vs last month (day-aligned same period) */}
            <div className={CARD_SHELL}>
                <div className="flex items-start justify-between">
                    <div>
                        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                            vs Last Month
                        </p>
                        <div className="mt-1 flex items-center gap-2">
                            <TrendIcon className={`h-5 w-5 ${trendColour}`} />
                            <span className={`text-2xl font-bold ${trendColour}`}>
                                {pctLabel(performance.amount_pct, amountDelta)}
                            </span>
                        </div>
                    </div>
                    <span className={`rounded-lg p-2 ${trendBg}`}>
                        <TrendingUp className={`h-4 w-4 ${trendColour}`} />
                    </span>
                </div>
                <div className="mt-2 space-y-0.5">
                    {/*
                      The absolute delta is always shown, which is what makes the
                      null-pct case ("New" / "—") still informative.
                    */}
                    <p className="text-[11px] text-gray-600">
                        {amountDelta >= 0 ? "+" : "−"}
                        {formatCompactAmount(Math.abs(amountDelta))} ·{" "}
                        {performance.count_delta >= 0 ? "+" : "−"}
                        {Math.abs(performance.count_delta).toLocaleString("en-IN")}{" "}
                        {Math.abs(performance.count_delta) === 1 ? "loan" : "loans"}{" "}
                        <span className="text-gray-400">
                            ({pctLabel(performance.count_pct, performance.count_delta)}{" "}
                            by count)
                        </span>
                    </p>
                    {/*
                      THE COMPARED PERIODS, SPELLED OUT. The day-aligned basis is
                      an explicit product decision and must never be ambiguous on
                      screen.
                    */}
                    <p className="text-[11px] font-medium text-gray-500">
                        {mtdRange} vs {lastRange}
                    </p>
                    <p className="text-[11px] text-gray-400">
                        Last month full month:{" "}
                        {formatCompactAmount(last_month_full.amount)} ·{" "}
                        {last_month_full.count.toLocaleString("en-IN")}{" "}
                        {last_month_full.count === 1 ? "loan" : "loans"}
                    </p>
                    {/* MTD pace vs full last month */}
                    <p className="text-[11px] font-medium text-orange-600">
                        MTD pace:{" "}
                        {mtdVsFullMultiplier !== null ? `${mtdVsFullMultiplier}x` : "—"}{" "}
                        full last month
                        {countMultiplier !== null && (
                            <span className="ml-1 font-normal text-gray-400">
                                ({countMultiplier}x by count)
                            </span>
                        )}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default DisbursedLoansKpiCards;
